import { useMemo, useState } from "react";

import { filtereOptionen, filterWerte, type KonfigurationsFeld, type KonfiguratorOption, type TabellenSpalte } from "@repo/shared";

import { euro } from "../../lib/regeln";
import { FeldRahmen } from "./FeldRahmen";

type AuswahlFeld = Extract<KonfigurationsFeld, { art: "auswahl" }>;

/** Mehr Zeilen auf einmal machen die Seite träge – darüber hilft nur ein engerer Filter. */
const MAX_ZEILEN = 200;

function zelle(option: KonfiguratorOption, spalte: TabellenSpalte): string {
  if (spalte.schluessel === "preis") {
    if (!option.preis) return "—";
    return `${euro(option.preis.nettoCents)}${option.preis.einheit ? ` / ${option.preis.einheit}` : ""}`;
  }
  const wert = option.merkmale?.[spalte.schluessel];
  return wert === null || wert === undefined || wert === "" ? "—" : String(wert);
}

/**
 * Große Auswahl als Tabelle mit Suche und Filtern – Spalten und Filter
 * beschreibt der Server. Ein Klick auf eine Zeile wählt die Option.
 */
export function AuswahlTabelle({
  feld,
  wert,
  onChange,
  beschriftungVerbergen,
}: {
  feld: AuswahlFeld & { tabelle: NonNullable<AuswahlFeld["tabelle"]> };
  wert: string;
  onChange: (wert: string) => void;
  beschriftungVerbergen?: boolean;
}) {
  const { tabelle, optionen } = feld;
  const [filter, setFilter] = useState<Record<string, string>>({});

  const auswahlWerte = useMemo(
    () => new Map(tabelle.filter.filter((f) => f.art !== "suche").map((f) => [f.schluessel, filterWerte(optionen, f)])),
    [optionen, tabelle],
  );
  // Ein gewählter Filterwert, den es nach einer Serverantwort nicht mehr gibt, wirkt nicht weiter –
  // sonst wäre die Tabelle gefiltert, während das Auswahlfeld „Alle“ zeigt.
  const wirksam = useMemo(() => {
    const ergebnis: Record<string, string> = {};
    for (const [schluessel, eingabe] of Object.entries(filter)) {
      const erlaubt = auswahlWerte.get(schluessel);
      if (!erlaubt || erlaubt.includes(eingabe)) ergebnis[schluessel] = eingabe;
    }
    return ergebnis;
  }, [filter, auswahlWerte]);
  const treffer = useMemo(() => filtereOptionen(optionen, tabelle, wirksam), [optionen, tabelle, wirksam]);

  const gewaehlt = optionen.find((o) => o.wert === wert);
  const gesperrt = feld.zustand.gesperrt;
  const setze = (schluessel: string, eingabe: string) => setFilter((alt) => ({ ...alt, [schluessel]: eingabe }));
  const waehle = (option: KonfiguratorOption) => {
    if (!gesperrt && !option.gesperrt) onChange(option.wert);
  };

  return (
    <FeldRahmen feld={feld} beschriftungVerbergen={beschriftungVerbergen}>
      {gewaehlt ? (
        <div className="konfi-gewaehlt">
          <span className="konfi-gewaehlt__label">Gewählt:</span>
          <strong>{gewaehlt.label}</strong>
          {gewaehlt.preis && (
            <span className="konfi-gewaehlt__preis">
              {euro(gewaehlt.preis.nettoCents)}
              {gewaehlt.preis.einheit ? ` / ${gewaehlt.preis.einheit}` : ""}
            </span>
          )}
        </div>
      ) : wert ? (
        <div className="konfi-gewaehlt konfi-gewaehlt--fehlt">
          <span className="konfi-gewaehlt__label">Gewählt:</span>
          <strong>nicht mehr verfügbar</strong>
          <span className="konfi-gewaehlt__preis">bitte neu wählen</span>
        </div>
      ) : (
        <div className="konfi-gewaehlt konfi-gewaehlt--leer">Noch nichts gewählt – Zeile anklicken.</div>
      )}

      {tabelle.filter.length > 0 && (
        <div className="konfi-tabellenfilter">
          {tabelle.filter.map((f) =>
            f.art === "suche" ? (
              <input
                key={f.schluessel}
                type="search"
                className="konfi-tabellenfilter__suche"
                placeholder={f.label}
                aria-label={f.label}
                value={filter[f.schluessel] ?? ""}
                onChange={(e) => setze(f.schluessel, e.target.value)}
              />
            ) : (
              <label key={f.schluessel} className="konfi-tabellenfilter__feld">
                <span>{f.label}</span>
                <select value={wirksam[f.schluessel] ?? ""} onChange={(e) => setze(f.schluessel, e.target.value)}>
                  <option value="">Alle</option>
                  {(auswahlWerte.get(f.schluessel) ?? []).map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
              </label>
            ),
          )}
        </div>
      )}

      <div className="tabelle-rahmen konfi-tabelle-rahmen">
        <table className="table konfi-tabelle">
          <thead>
            <tr>
              {tabelle.spalten.map((s) => (
                <th key={s.schluessel} className={s.ausrichtung === "rechts" ? "num" : undefined}>
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {treffer.slice(0, MAX_ZEILEN).map((option) => {
              const aktiv = option.wert === wert;
              const klassen = ["konfi-zeile"];
              if (aktiv) klassen.push("konfi-zeile--gewaehlt");
              if (option.gesperrt || gesperrt) klassen.push("konfi-zeile--gesperrt");
              return (
                <tr
                  key={option.wert}
                  className={klassen.join(" ")}
                  tabIndex={option.gesperrt || gesperrt ? -1 : 0}
                  aria-current={aktiv ? "true" : undefined}
                  title={option.gesperrt && option.hinweis ? option.hinweis : undefined}
                  onClick={() => waehle(option)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      waehle(option);
                    }
                  }}
                >
                  {tabelle.spalten.map((s) => (
                    <td key={s.schluessel} className={s.ausrichtung === "rechts" ? "num" : undefined}>
                      {zelle(option, s)}
                    </td>
                  ))}
                </tr>
              );
            })}
            {treffer.length === 0 && (
              <tr>
                <td colSpan={tabelle.spalten.length} className="muted">
                  Keine Treffer – Filter lockern.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <small className="muted">
        {treffer.length > MAX_ZEILEN
          ? `Weitere Treffer – Filter einschränken (${MAX_ZEILEN} von ${treffer.length})`
          : `${treffer.length} von ${optionen.length}`}
      </small>
    </FeldRahmen>
  );
}
